import React from 'react';

/* The header's density control. Three positions on a 1px --border-default track
   over --bg-inset, active segment --accent-subtle with --accent text.
   It affects TABLES ONLY — tiles, hero figures, prose and forms never change
   size, because compressing those compresses the judgement.

   ## The thumb slides

   Machined register, 2026-08-01. Same technique as `TabBar`: one absolutely
   positioned element placed with `translateX(...) scaleX(...)`, so travel and resize
   both run on the compositor; measured with a ResizeObserver and re-measured once the
   webfont lands.

   Worth checking against the rule that frequently-used controls should not animate.
   Density is a per-screen preference — set once when the operator decides how they
   want to read that screen, then remembered. That makes it occasional, not a hot path
   like the command palette, so it gets the standard treatment. The distinction the
   motion system draws is **frequency of use**, which is not the same as how often the
   control is on screen, and the question has to be asked rather than assumed. */

export function SegmentedControl({ options = [], value, onChange, label, style }) {
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const trackRef = React.useRef(null);
  const [thumb, setThumb] = React.useState(null);
  const [ready, setReady] = React.useState(false);

  const measure = React.useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const el = track.querySelector('[data-on]');
    const next = el ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight } : null;
    // Bail on an unchanged measurement — see the same guard in TabBar for why.
    setThumb((prev) => (
      prev && next && prev.x === next.x && prev.y === next.y && prev.w === next.w && prev.h === next.h
        ? prev : next
    ));
  }, []);

  React.useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    let cancelled = false;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (!cancelled) measure(); });
    }
    return () => { cancelled = true; ro.disconnect(); };
  }, [measure, options, value]);

  React.useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div role="group" aria-label={label} ref={trackRef} style={{
      position: 'relative', display: 'inline-flex', height: 28, padding: 1, background: 'var(--bg-inset)',
      border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)',
      boxShadow: 'var(--edge-inset)', ...style,
    }}>
      {thumb ? (
        <span aria-hidden="true" className="jmt-seg-thumb" style={{
          position: 'absolute', left: 0, top: thumb.y, width: 1, height: thumb.h,
          background: 'var(--accent-subtle)', borderRadius: 5, transformOrigin: 'left',
          transform: `translateX(${thumb.x}px) scaleX(${thumb.w})`,
          transition: ready ? 'transform var(--dur-menu) var(--ease-in-out)' : 'none',
        }} />
      ) : null}
      {opts.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange && onChange(o.value)}
            aria-pressed={on} title={o.title} data-on={on ? '' : undefined} className="jmt-seg"
            style={{
              position: 'relative', zIndex: 1,
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 10px', border: 'none',
              borderRadius: 5, cursor: 'pointer', background: 'transparent',
              color: on ? 'var(--accent)' : 'var(--text-secondary)',
              fontFamily: 'var(--font-ui)', fontSize: 'var(--label-size)', fontWeight: 'var(--label-weight)',
              letterSpacing: 'var(--label-tracking)', textTransform: 'uppercase',
              transition: 'color var(--dur-state) var(--ease-out)',
            }}>{o.label}</button>
        );
      })}
      <style>{`
        .jmt-seg:hover:not([data-on]){color:var(--text-primary)}
        .jmt-seg:focus-visible{outline:none;box-shadow:var(--focus-ring)}
        @media (prefers-reduced-motion: reduce){.jmt-seg-thumb{transition:none}}
      `}</style>
    </div>
  );
}
